import { useCallback } from "react";
import { SETTING_DEFS } from "@gaole/shared";
import { Button, Input, SettingsCard, Toggle } from "../../components/ui";
import { ImageUploadField } from "../../components/ImageUploadField";
import { settingsTabLabel } from "../../config/admin-settings";

const COMPANY_KEYS = SETTING_DEFS.filter((d) => d.group === "company").map((d) => d.key);

interface CompanySettingsEditorProps {
  draft: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  saveMut: { mutate: (keys: string[]) => void; isPending: boolean };
}

export function CompanySettingsEditor({ draft, setValue, saveMut }: CompanySettingsEditorProps) {
  const logoUrls = String(draft["company.logo_file_key"] ?? "").trim()
    ? [String(draft["company.logo_file_key"])]
    : [];

  const setLogo = useCallback(
    (urls: string[]) => setValue("company.logo_file_key", urls[0] ?? ""),
    [setValue],
  );

  return (
    <div className="w-full min-w-0 space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">{settingsTabLabel("company")}</h1>
        <p className="mt-0.5 text-xs text-muted">
          Legal entity and tax details shown on tax invoices and payment receipts.
        </p>
      </div>

      <SettingsCard
        title="Company"
        description="Registered business identity on printed documents."
        footer={
          <div className="flex justify-end">
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate(COMPANY_KEYS)}>
              Save
            </Button>
          </div>
        }
      >
        <ImageUploadField
          label="Logo"
          hint="Shown on invoice and receipt PDFs. PNG or JPG recommended."
          value={logoUrls}
          onChange={setLogo}
          max={1}
        />
        <Input
          label="Legal name"
          value={String(draft["company.legal_name"] ?? "")}
          onChange={(e) => setValue("company.legal_name", e.target.value)}
        />
        <div className="form-row-2">
          <Input
            label="Registration no."
            value={String(draft["company.reg_no"] ?? "")}
            onChange={(e) => setValue("company.reg_no", e.target.value)}
            hint='Shown as "(Co. No.: …)" on PDF header.'
          />
          <Input
            label="Phone"
            value={String(draft["company.phone"] ?? "")}
            onChange={(e) => setValue("company.phone", e.target.value)}
          />
          <Input
            label="WhatsApp number"
            hint="Digits only with country code (e.g. 60123456789). Home page chat button."
            value={String(draft["company.whatsapp"] ?? "")}
            onChange={(e) => setValue("company.whatsapp", e.target.value)}
          />
        </div>
        <Input
          label="Email"
          type="email"
          value={String(draft["company.email"] ?? "")}
          onChange={(e) => setValue("company.email", e.target.value)}
        />
        <Input
          label="Address line 1"
          value={String(draft["company.address1"] ?? "")}
          onChange={(e) => setValue("company.address1", e.target.value)}
        />
        <Input
          label="Address line 2"
          value={String(draft["company.address2"] ?? "")}
          onChange={(e) => setValue("company.address2", e.target.value)}
        />
        <div className="form-row-3">
          <Input
            label="Postcode"
            value={String(draft["company.postcode"] ?? "")}
            onChange={(e) => setValue("company.postcode", e.target.value)}
          />
          <Input
            label="City"
            value={String(draft["company.city"] ?? "")}
            onChange={(e) => setValue("company.city", e.target.value)}
          />
          <Input
            label="State"
            value={String(draft["company.state"] ?? "")}
            onChange={(e) => setValue("company.state", e.target.value)}
          />
        </div>
        <Input
          label="Country"
          value={String(draft["company.country"] ?? "")}
          onChange={(e) => setValue("company.country", e.target.value)}
        />
      </SettingsCard>

      <SettingsCard
        title="Tax"
        description="SST/GST registration, rate, and label used on invoice and receipt PDFs."
        footer={
          <div className="flex justify-end">
            <Button size="sm" disabled={saveMut.isPending} onClick={() => saveMut.mutate(COMPANY_KEYS)}>
              Save
            </Button>
          </div>
        }
      >
        <Input
          label="Tax registration no. (SST/GST)"
          value={String(draft["company.tax_id"] ?? "")}
          onChange={(e) => setValue("company.tax_id", e.target.value)}
          hint="Printed in the document header next to company registration when set."
        />
        <Input
          label="Tax label on documents"
          value={String(draft["company.tax_label"] ?? "SST")}
          onChange={(e) => setValue("company.tax_label", e.target.value)}
          hint='Row label on PDF totals (e.g. "SST", "GST").'
        />
        <Input
          label="Tax rate (%)"
          type="number"
          step="0.01"
          value={draft["company.tax_rate"] === "" || draft["company.tax_rate"] == null ? "" : String(draft["company.tax_rate"])}
          onChange={(e) => setValue("company.tax_rate", e.target.value)}
          hint="e.g. 6 for 6% SST. Used on document totals when enabled in the template."
        />
        <Toggle
          label="Prices include tax"
          checked={Boolean(draft["company.tax_inclusive"])}
          onChange={(v) => setValue("company.tax_inclusive", v)}
        />
      </SettingsCard>

      <SettingsCard title="Footer">
        <Input
          label="Default footer note"
          value={String(draft["company.invoice_footer"] ?? "")}
          onChange={(e) => setValue("company.invoice_footer", e.target.value)}
          hint="Terms, bank details, or thank-you message. Can be overridden per template block."
        />
      </SettingsCard>
    </div>
  );
}
